import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, ReactNode } from 'react'
import { toast } from '@/hooks/use-toast'

export interface BackgroundJob {
  id: string
  label: string
  status: 'running' | 'done' | 'error'
  startedAt: number
  finishedAt?: number
  error?: string
  /** Optional deep link back to where this job's result actually lives (e.g.
   * a generated report's download URL) — shown as "View result" in the
   * dropdown. Only meaningful for results that persist somewhere after the
   * page that started them is gone; omit it rather than link somewhere that
   * won't actually show the result (see summary below for that case). */
  href?: string
  /** Optional one-line result summary shown in place of "Complete" — for
   * jobs whose result only ever lived in the triggering page's own state
   * (e.g. a batch-screen result object with no persistence anywhere), so
   * there's still SOME visible outcome after navigating away, even without
   * anywhere to link to. */
  summary?: string
}

interface BackgroundJobsContextType {
  jobs: BackgroundJob[]
  runningCount: number
  startJob: (label: string) => string
  completeJob: (id: string, opts?: { href?: string; summary?: string }) => void
  failJob: (id: string, error: string) => void
  runJob: <T>(
    label: string,
    fn: () => Promise<T>,
    opts?: { href?: (result: T) => string | undefined; summary?: (result: T) => string | undefined },
  ) => Promise<T>
  dismissJob: (id: string) => void
}

const BackgroundJobsContext = createContext<BackgroundJobsContextType | undefined>(undefined)

// How long a finished job stays visible in the dropdown before it's swept —
// long enough to notice it if you weren't watching, short enough that the
// list doesn't accumulate stale entries all day.
const FINISHED_JOB_TTL_MS = 60_000

let idCounter = 0
function nextId() {
  idCounter += 1
  return `job-${Date.now()}-${idCounter}`
}

export function BackgroundJobsProvider({ children }: { children: ReactNode }) {
  const [jobs, setJobs] = useState<BackgroundJob[]>([])
  const jobsRef = useRef(jobs)
  jobsRef.current = jobs

  const startJob = useCallback((label: string) => {
    const id = nextId()
    setJobs((prev) => [...prev, { id, label, status: 'running', startedAt: Date.now() }])
    return id
  }, [])

  const completeJob = useCallback((id: string, opts?: { href?: string; summary?: string }) => {
    setJobs((prev) => prev.map((j) => (j.id === id ? { ...j, status: 'done', finishedAt: Date.now(), href: opts?.href, summary: opts?.summary } : j)))
    const job = jobsRef.current.find((j) => j.id === id)
    toast({ title: 'Task complete', description: opts?.summary ?? job?.label ?? 'A background task finished.' })
    setTimeout(() => setJobs((prev) => prev.filter((j) => j.id !== id)), FINISHED_JOB_TTL_MS)
  }, [])

  const failJob = useCallback((id: string, error: string) => {
    setJobs((prev) => prev.map((j) => (j.id === id ? { ...j, status: 'error', finishedAt: Date.now(), error } : j)))
    const job = jobsRef.current.find((j) => j.id === id)
    toast({ title: 'Task failed', description: job ? `${job.label}: ${error}` : error, variant: 'destructive' })
    setTimeout(() => setJobs((prev) => prev.filter((j) => j.id !== id)), FINISHED_JOB_TTL_MS)
  }, [])

  const dismissJob = useCallback((id: string) => {
    setJobs((prev) => prev.filter((j) => j.id !== id))
  }, [])

  const runJob = useCallback(async <T,>(
    label: string,
    fn: () => Promise<T>,
    opts?: { href?: (result: T) => string | undefined; summary?: (result: T) => string | undefined },
  ): Promise<T> => {
    const id = startJob(label)
    try {
      const result = await fn()
      completeJob(id, { href: opts?.href?.(result), summary: opts?.summary?.(result) })
      return result
    } catch (err) {
      failJob(id, err instanceof Error ? err.message : 'Failed')
      throw err
    }
  }, [startJob, completeJob, failJob])

  const runningCount = useMemo(() => jobs.filter((j) => j.status === 'running').length, [jobs])

  // Browsers only let JS intercept an actual close/reload/navigate-away —
  // never a tab switch — so this is the one real "warning" available: if you
  // try to close or refresh this tab while something is still running, the
  // browser's own native confirm dialog fires. Switching tabs (or to another
  // app) can't be intercepted by any site; the persistent indicator below is
  // what covers that case — check it when you come back.
  useEffect(() => {
    function handleBeforeUnload(e: BeforeUnloadEvent) {
      if (runningCount > 0) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [runningCount])

  const value = useMemo(
    () => ({ jobs, runningCount, startJob, completeJob, failJob, runJob, dismissJob }),
    [jobs, runningCount, startJob, completeJob, failJob, runJob, dismissJob],
  )

  return <BackgroundJobsContext.Provider value={value}>{children}</BackgroundJobsContext.Provider>
}

export function useBackgroundJobs() {
  const ctx = useContext(BackgroundJobsContext)
  if (!ctx) throw new Error('useBackgroundJobs must be used within a BackgroundJobsProvider')
  return ctx
}
